import {
  CutlistMaterialGroup,
  CutlistOrder,
  MaterialGroup,
  PartItem,
} from '@cutr/constants/cutlist';
import { NestedLayout } from '@cutr/nesting';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type {
  NestingJobGroupStatus,
  PollNestingJobsResponse,
} from '@/api/backend/cutlists';
import { hashGroupForNesting } from '@/api/materialsGroup';
import { hashObject } from '@/utils/misc';

const POLL_INTERVAL_MS = 2000;

export const isNestingPollSettled = (
  response: PollNestingJobsResponse
): boolean =>
  response.done ||
  response.groups.every(
    (group) => group.status === 'succeeded' || group.status === 'failed'
  );

export const nestingPollInterval = (
  response: PollNestingJobsResponse | undefined
): number | false =>
  response && isNestingPollSettled(response) ? false : POLL_INTERVAL_MS;

export type MixedNestingState = 'idle' | 'computing' | 'done' | 'failed';

export type MixedStockNestingGroup = {
  id: string;
  layouts: NestedLayout[];
};

type MixedNestingStore = {
  nestingState: MixedNestingState;
  polling: boolean;
  snapshotHash: number | null;
  groups: MixedStockNestingGroup[];
  failedGroups: NestingJobGroupStatus[] | null;
  setNestingState(nestingState: MixedNestingState): void;
  setPolling(polling: boolean): void;
  setSnapshotHash(snapshotHash: number | null): void;
  setFailedGroups(failedGroups: NestingJobGroupStatus[] | null): void;
  setResults(cutlist: CutlistOrder): void;
  reset(): void;
};

const DEFAULT_STATE = {
  nestingState: 'idle' as const,
  polling: false,
  snapshotHash: null,
  groups: [] as MixedStockNestingGroup[],
  failedGroups: null,
};

export const useMixedNestingStore = create<MixedNestingStore>()(
  devtools(
    (set) => ({
      ...DEFAULT_STATE,
      setNestingState: (nestingState) =>
        set(() =>
          nestingState === 'failed'
            ? { nestingState }
            : { nestingState, failedGroups: null }
        ),
      setPolling: (polling) => set(() => ({ polling })),
      setSnapshotHash: (snapshotHash) => set(() => ({ snapshotHash })),
      setFailedGroups: (failedGroups) => set(() => ({ failedGroups })),
      setResults: (cutlist) =>
        set(() => ({
          groups: cutlist.materialGroups
            .map((group) => ({
              id: group.groupId,
              output: group.nestingOutput ?? null,
            }))
            .filter(
              (group) =>
                group.output &&
                'layouts' in group.output &&
                group.output.layouts?.length
            )
            .map((group) => ({
              id: group.id,
              layouts:
                group.output && 'layouts' in group.output
                  ? group.output.layouts
                  : [],
            })),
        })),
      reset: () => set(() => ({ ...DEFAULT_STATE })),
    }),
    { name: 'mixed-nesting-store' }
  )
);

const groupNeedsNesting = (group: CutlistMaterialGroup) =>
  (group.parts ?? []).some(
    (part) =>
      part.partType !== 'beam' &&
      part.widthMM > 0 &&
      part.lengthMM > 0 &&
      part.quantity > 0
  );

export const isCutlistFullyNested = (cutlist: CutlistOrder) =>
  cutlist.materialGroups.every(
    (group) => Boolean(group.nestingOutput) || !groupNeedsNesting(group)
  );

export const persistedNestingFailures = (
  cutlist: CutlistOrder
): NestingJobGroupStatus[] =>
  cutlist.materialGroups.flatMap((group) =>
    group.nestingError
      ? [
          {
            groupId: group.groupId,
            status: 'failed' as const,
            error: group.nestingError,
          },
        ]
      : []
  );

export const computeCutlistNestingHash = (
  groups: MaterialGroup[],
  parts: PartItem[]
) =>
  hashObject(
    groups.map((group) =>
      hashGroupForNesting(
        group,
        parts.filter((part) => part.groupId === group.id)
      )
    )
  );
